import classnames from 'classnames'; //eslint-disable-line
import { isEmpty, orderBy } from 'lodash';
import parse from 'html-react-parser';
import './editor.scss';
import {
  InspectorControlsAppearance,
  InspectorControlsGeneral,
  InspectorTabs,
} from '@components/inspector-tabs';
import ClassDropdown, { getBlockClassNames } from '@components/class-dropdown';
import Visibility from '@components/visibility';
import BlockControlSettings from './block-controllers';
import CodeInserterModal from './icon-inserter/code-inserter';
import IconLibraryInserterModal from './icon-inserter/icon-library-inserter';
import getIcons from './icons';
import { DEFAULT_CSS_CLASS } from '@constants';
import Label from '@components/block-label';

const {
  i18n: { __ },
  element: { useState },
  blockEditor: { useBlockProps, useSetting },
  components: {
    PanelBody,
    Panel,
    ToggleControl,
    __experimentalNumberControl: NumberControl,
  },
} = wp;

const InspectorGeneral = (props) => {
  const {
    attributes: { blockVisibility, label },
    setAttributes,
  } = props;

  return (
    <InspectorControlsGeneral>
      <Label {...{ label, setAttributes }} />
      <Visibility {...{ blockVisibility, setAttributes }} />
    </InspectorControlsGeneral>
  );
};

const InspectorAppearance = (props) => {
  const {
    name,
    attributes: { blockClassNames, enableWidth, width },
    setAttributes,
  } = props;

  const themeSettingsEnableSetWidth = useSetting(
    `custom.blocks.${name}.enableSetWidth`,
  );

  return (
    <InspectorControlsAppearance>
      {themeSettingsEnableSetWidth && (
        <Panel>
          <PanelBody title={__('Width', 'b3rg')}>
            <ToggleControl
              label="Enable/Disable"
              checked={enableWidth}
              onChange={
                (enableWidth) => setAttributes({ enableWidth }) //eslint-disable-line
              }
            />
            {enableWidth && (
              <NumberControl
                isShiftStepEnabled={true}
                onChange={
                  (width) => setAttributes({ width }) //eslint-disable-line
                }
                shiftStep={1}
                value={width}
              />
            )}
          </PanelBody>
        </Panel>
      )}
      <ClassDropdown
        {...{
          blockClassNames,
          setAttributes,
          blockClassPrefix: DEFAULT_CSS_CLASS.ICON,
        }}
      />
    </InspectorControlsAppearance>
  );
};

const Edit = (props) => {
  const { attributes, setAttributes } = props;
  const {
    blockVisibility,
    blockClassNames,
    flipHorizontal,
    flipVertical,
    rotate,
    iconName,
    iconExtension,
    icon,
    enableWidth,
    width,
  } = attributes;

  const [isIconLibraryInserterOpen, setIconLibraryInserterOpen] =
    useState(false);
  const [isCodeInserterOpen, setCodeInserterOpen] = useState(false);

  //Get All icons
  const allIcons = orderBy(getIcons(), 'name', 'asc');

  const namedIcon = allIcons.filter((i) => i.name === iconName);
  const printedIcon = namedIcon[0]?.icon || '';

  let renderContent = '';
  if (icon) {
    renderContent = parse(icon);
  } else if (!isEmpty(iconExtension) && iconName) {
    renderContent = <img src={iconName} alt="" />;
  } else if (iconName) {
    renderContent = printedIcon;
  }

  // set block classes and set to blockProps
  let classes = getBlockClassNames(blockClassNames);
  const iconClasses = classnames({
    [`rotate-${rotate}`]: rotate,
    'flip-horizontal': flipHorizontal,
    'flip-vertical': flipVertical,
    disable: !blockVisibility,
  });

  if (iconClasses) {
    classes = `${classes} ${iconClasses}`;
  }

  const blockProps = useBlockProps({
    className: classes,
  });

  const iconStyles = enableWidth ? { width: `${width}px` } : null;

  const prop = {
    flipHorizontal,
    flipVertical,
    rotate,
    enableWidth,
    setAttributes,
    setCodeInserterOpen,
    setIconLibraryInserterOpen,
  };

  return (
    <>
      <BlockControlSettings {...prop} />
      <InspectorTabs>
        <InspectorGeneral {...props} />
        <InspectorAppearance {...props} />
      </InspectorTabs>
      {isCodeInserterOpen && (
        <CodeInserterModal
          setCodeInserterOpen={setCodeInserterOpen}
          attributes={attributes}
          setAttributes={setAttributes}
        />
      )}
      {isIconLibraryInserterOpen && (
        <IconLibraryInserterModal
          setIconLibraryInserterOpen={setIconLibraryInserterOpen}
          attributes={attributes}
          setAttributes={setAttributes}
          allIcons={allIcons}
        />
      )}
      <div {...blockProps} style={iconStyles}>
        {renderContent}
      </div>
    </>
  );
};

export default Edit;
